import React, { useEffect, useState } from 'react';
import './App.css';

function TermsConditions({ onBack }) {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="App">
      <div className="container legal-page-container">
        {/* Header */}
        <div className="legal-header">
          <div className="legal-header-top">
            <div className="logo-wrapper">
              <div className="logo-icon">
                <svg viewBox="0 0 64 72" xmlns="http://www.w3.org/2000/svg" aria-label="Algodentra logo">
                  <defs>
                    <linearGradient id="cartoonToothSm" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="rgba(255,255,255,0.96)"/>
                      <stop offset="100%" stopColor="rgba(255,255,255,0.82)"/>
                    </linearGradient>
                    <linearGradient id="pearlLineSm" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#a5b4fc"/>
                      <stop offset="100%" stopColor="#c7d2fe"/>
                    </linearGradient>
                  </defs>
                  <path
                    d="M18 14c4.5-4.5 23.5-4.5 28 0 4 4 4.5 10 2.5 17-2.2 7.6-6.7 22-8.6 28.4-.7 2.2-3.5 2.4-4.6 0.4l-2.8-5.1c-.8-1.5-3-1.5-3.8 0l-2.8 5.1c-1.1 2-3.9 1.8-4.6-0.4-1.9-6.4-6.4-20.8-8.6-28.4C13.5 24 14 18 18 14Z"
                    fill="url(#cartoonToothSm)"
                    stroke="rgba(37,99,235,0.85)"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="32"
                    cy="28.5"
                    r="8"
                    fill="none"
                    stroke="url(#pearlLineSm)"
                    strokeWidth="2"
                  />
                  <circle cx="28.5" cy="25.5" r="1.6" fill="#ffffff" opacity="0.9"/>
                  <path d="M32 20 L32 14" stroke="url(#pearlLineSm)" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M26 31 L18 34" stroke="url(#pearlLineSm)" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M38 31 L46 34" stroke="url(#pearlLineSm)" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M28 25 L21 20" stroke="url(#pearlLineSm)" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M36 25 L43 20" stroke="url(#pearlLineSm)" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M32 22 L32 16" stroke="url(#pearlLineSm)" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M30 36 L30 42" stroke="url(#pearlLineSm)" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M34 36 L34 42" stroke="url(#pearlLineSm)" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              </div>
              <h1 className="title">Algodentra</h1>
            </div>
            <button className="auth-back-button" onClick={onBack}>
              <span className="auth-back-icon">←</span>
              Back to login
            </button>
          </div>
          <div className="divider"></div>
        </div>

        {/* Content */}
        <div className="legal-content">
          <h1 className="legal-title">Terms and Conditions</h1>
          <p className="legal-date"><strong>Last Updated:</strong> November 9, 2025</p>

          <div className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using Algodentra ("the Platform," "Service," "we," "our," or "us"), you ("User," "you," or "your") agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you must not use the Platform.
            </p>
            <p>
              <strong>Important:</strong> This is a legally binding agreement. Please read carefully.
            </p>
            <p className="warning-text">
              <strong>Compliance Status:</strong> Algodentra is privacy-first by design (local processing and PII redaction), but it is <strong>not yet deployed on HIPAA-BAA infrastructure</strong>. Until funding allows migration to a compliant, BAA-backed stack, do not treat this service as HIPAA/PIPEDA/PHIPA certified and avoid uploading PHI that requires a BAA.
            </p>
          </div>

          <div className="legal-section">
            <h2>2. Eligibility and User Accounts</h2>

            <h3>2.1 Professional Use Only</h3>
            <p>
              Algodentra is designed exclusively for licensed healthcare professionals, including but not limited to:
            </p>
            <ul>
              <li>Dentists and dental specialists</li>
              <li>Physicians and medical practitioners</li>
              <li>Nurse practitioners and physician assistants</li>
              <li>Other licensed clinical professionals</li>
            </ul>

            <h3>2.2 Authorized Users</h3>
            <p>You represent and warrant that:</p>
            <ul>
              <li>You are at least 18 years of age</li>
              <li>You hold a valid professional license in good standing</li>
              <li>You have the legal authority to enter into this agreement</li>
              <li>All information you provide is accurate and complete</li>
            </ul>

            <h3>2.3 Access Codes</h3>
            <ul>
              <li>Access codes are confidential and must not be shared</li>
              <li>You are responsible for all activity under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>We reserve the right to revoke access at any time</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>3. Description of Service</h2>

            <h3>3.1 Core Features</h3>
            <p>Algodentra provides the following services:</p>
            <ul>
              <li><strong>Audio Transcription:</strong> Convert patient consultation recordings to text using AI</li>
              <li><strong>PII Redaction:</strong> Automatically identify and redact personally identifiable information</li>
              <li><strong>SOAP Note Generation:</strong> AI-powered creation of structured clinical documentation</li>
              <li><strong>Clinical Support:</strong> Evidence-based recommendations and billing code suggestions</li>
              <li><strong>Note Management:</strong> Save, edit, and retrieve SOAP notes</li>
            </ul>

            <h3>3.2 Service Availability</h3>
            <ul>
              <li>We strive for 99.9% uptime but do not guarantee uninterrupted service</li>
              <li>Scheduled maintenance will be announced in advance</li>
              <li>Emergency maintenance may occur without notice</li>
              <li>We are not liable for service disruptions beyond our control</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>4. AI-Generated Content and Limitations</h2>

            <h3>4.1 No Medical Advice</h3>
            <p className="warning-text">
              <strong>CRITICAL DISCLAIMER:</strong> Algodentra is a documentation tool, NOT a diagnostic or treatment tool. All AI-generated content is for informational purposes only and does not constitute medical advice.
            </p>

            <h3>4.2 Professional Responsibility</h3>
            <ul>
              <li>You remain solely responsible for all clinical decisions</li>
              <li>AI-generated SOAP notes must be reviewed and verified before use</li>
              <li>Billing codes are unvalidated suggestions and must be confirmed</li>
              <li>You must use your professional judgment in all patient care</li>
            </ul>

            <h3>4.3 Accuracy and Reliability</h3>
            <ul>
              <li>AI systems may produce errors, omissions, or inaccuracies</li>
              <li>Transcription accuracy depends on audio quality and clarity</li>
              <li>PII redaction may not catch all sensitive information</li>
              <li>Clinical recommendations are based on general best practices, not patient-specific data</li>
            </ul>

            <h3>4.4 User Verification Required</h3>
            <p>You must:</p>
            <ul>
              <li>Review all AI-generated content for accuracy</li>
              <li>Verify all clinical information before documentation</li>
              <li>Confirm all billing codes against current fee schedules</li>
              <li>Edit and supplement notes as needed for completeness</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>5. Patient Consent and Legal Compliance</h2>

            <h3>5.1 Your Obligations</h3>
            <p>You are responsible for:</p>
            <ul>
              <li>Obtaining patient consent for recording consultations</li>
              <li>Complying with local recording laws and regulations</li>
              <li>Informing patients that AI services will process their information</li>
              <li>Maintaining patient confidentiality at all times</li>
            </ul>

            <h3>5.2 Regulatory Compliance</h3>
            <p>You must comply with all applicable laws, including:</p>
            <ul>
              <li>HIPAA (United States) — note: the Platform is not yet hosted on a HIPAA-compliant/BAA environment; you must ensure PHI that requires a BAA is not uploaded until that deployment is complete.</li>
              <li>PIPEDA and PHIPA (Canada)</li>
              <li>State/Provincial privacy laws</li>
              <li>Professional licensing requirements</li>
              <li>Medical record documentation standards</li>
            </ul>

            <h3>5.3 Prohibited Uses</h3>
            <p>You may NOT use Algodentra to:</p>
            <ul>
              <li>Process information from minors without guardian consent</li>
              <li>Record patients without their knowledge or consent</li>
              <li>Share patient data with unauthorized parties</li>
              <li>Use the service for non-clinical purposes</li>
              <li>Circumvent security or access controls</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>6. Intellectual Property</h2>

            <h3>6.1 Ownership</h3>
            <ul>
              <li><strong>Platform:</strong> We own all rights to the Algodentra platform, code, and branding</li>
              <li><strong>Your Content:</strong> You retain ownership of all patient data and clinical content you input</li>
              <li><strong>Generated Notes:</strong> You own the SOAP notes generated from your patient data</li>
              <li><strong>AI Models:</strong> Third-party AI providers retain rights to their models and systems</li>
            </ul>

            <h3>6.2 License to Use Your Data</h3>
            <p>You grant us a limited license to:</p>
            <ul>
              <li>Process your audio and text data to generate SOAP notes</li>
              <li>Use de-identified, aggregated data to improve AI accuracy</li>
              <li>Store your content as necessary to provide the service</li>
            </ul>
            <p><strong>We will NOT:</strong></p>
            <ul>
              <li>Share your patient data with third parties (no BAAs are in place today; cloud processing is limited to de-identified/redacted data until BAA-backed hosting is live)</li>
              <li>Use identifiable patient information for marketing or research</li>
              <li>Sell or monetize your data</li>
            </ul>

            <h3>6.3 Restrictions</h3>
            <p>You may NOT:</p>
            <ul>
              <li>Reverse engineer, decompile, or disassemble the Platform</li>
              <li>Remove copyright, trademark, or proprietary notices</li>
              <li>Copy, modify, or create derivative works</li>
              <li>Resell or redistribute the service</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>7. Fees and Payment</h2>

            <h3>7.1 Subscription Model</h3>
            <ul>
              <li>Pricing is subject to the plan selected at signup</li>
              <li>Fees are billed in advance on a monthly or annual basis</li>
              <li>All fees are non-refundable unless otherwise stated</li>
              <li>We reserve the right to change pricing with 30 days notice</li>
            </ul>

            <h3>7.2 Taxes</h3>
            <ul>
              <li>You are responsible for all applicable taxes</li>
              <li>Sales tax, GST, HST, or VAT may apply based on jurisdiction</li>
            </ul>

            <h3>7.3 Late Payment</h3>
            <ul>
              <li>Accounts 15+ days overdue may be suspended</li>
              <li>Accounts 30+ days overdue may be terminated</li>
              <li>A late fee of 1.5% per month may apply</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>8. Termination</h2>

            <h3>8.1 Termination by You</h3>
            <ul>
              <li>You may cancel your account at any time</li>
              <li>No refunds for partial months or annual subscriptions</li>
              <li>Your data will be retained for 90 days after cancellation</li>
              <li>Request data export before account closure</li>
            </ul>

            <h3>8.2 Termination by Us</h3>
            <p>We may suspend or terminate your account if:</p>
            <ul>
              <li>You violate these Terms</li>
              <li>Your payment method fails or account is overdue</li>
              <li>You engage in fraudulent or illegal activity</li>
              <li>We discontinue the service (with 60 days notice)</li>
            </ul>

            <h3>8.3 Effect of Termination</h3>
            <ul>
              <li>Your access to the Platform will cease immediately</li>
              <li>All outstanding fees become due immediately</li>
              <li>We will delete your data per our retention policy</li>
              <li>Provisions that should survive (liability, indemnity) remain in effect</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>9. Disclaimer of Warranties</h2>
            <p className="warning-text">
              <strong>THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.</strong>
            </p>
            <p>We disclaim all warranties, including but not limited to:</p>
            <ul>
              <li>Merchantability and fitness for a particular purpose</li>
              <li>Non-infringement of third-party rights</li>
              <li>Accuracy, reliability, or completeness of AI-generated content</li>
              <li>Uninterrupted, secure, or error-free service</li>
              <li>Results obtained from using the Platform</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>10. Limitation of Liability</h2>

            <h3>10.1 No Liability for Indirect Damages</h3>
            <p className="warning-text">
              <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR:</strong>
            </p>
            <ul>
              <li>Indirect, incidental, special, or consequential damages</li>
              <li>Loss of profits, revenue, data, or business opportunities</li>
              <li>Medical malpractice or professional liability claims</li>
              <li>Damages arising from your use of AI-generated content</li>
              <li>Errors, omissions, or inaccuracies in SOAP notes</li>
            </ul>

            <h3>10.2 Cap on Liability</h3>
            <p>
              We do not agree to pay any capped amounts; your sole remedy is to stop using the Platform where permitted by law.
            </p>

            <h3>10.3 Exceptions</h3>
            <p>These limitations do not apply to:</p>
            <ul>
              <li>Our gross negligence or willful misconduct</li>
              <li>Data breaches caused by our failure to implement reasonable security</li>
              <li>Liabilities that cannot be excluded under applicable law</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Algodentra, its officers, directors, employees, and agents from any claims, damages, liabilities, costs, or expenses (including legal fees) arising from:
            </p>
            <ul>
              <li>Your use or misuse of the Platform</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any law or regulation</li>
              <li>Medical malpractice or professional negligence claims</li>
              <li>Failure to obtain patient consent for recordings</li>
              <li>Unauthorized disclosure of patient information</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>12. Dispute Resolution</h2>

            <h3>12.1 Informal Resolution</h3>
            <p>Before filing a claim, you agree to try to resolve disputes informally by contacting us at pearlaicare@gmail.com.</p>

            <h3>12.2 Arbitration</h3>
            <ul>
              <li>Disputes will be resolved through binding arbitration, not in court</li>
              <li>Arbitration follows the rules of the American Arbitration Association</li>
              <li>Arbitration takes place in [Your Jurisdiction]</li>
              <li>Each party bears their own costs unless otherwise awarded</li>
            </ul>

            <h3>12.3 Class Action Waiver</h3>
            <p>
              You agree to resolve disputes individually and waive any right to participate in class actions or representative proceedings.
            </p>

            <h3>12.4 Exceptions</h3>
            <p>The following disputes are excluded from arbitration:</p>
            <ul>
              <li>Intellectual property infringement claims</li>
              <li>Small claims court matters (under jurisdictional limits)</li>
              <li>Injunctive relief for contract breaches</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>13. Governing Law</h2>
            <p>
              These Terms are governed by the laws of [Your State/Province] and Canada/United States, without regard to conflict of law principles.
            </p>
            <p>
              You consent to the exclusive jurisdiction of courts in [Your Jurisdiction] for any disputes not subject to arbitration.
            </p>
          </div>

          <div className="legal-section">
            <h2>14. Miscellaneous</h2>

            <h3>14.1 Entire Agreement</h3>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Algodentra.
            </p>

            <h3>14.2 Modifications</h3>
            <p>
              We may modify these Terms at any time. Material changes will be effective 30 days after notice. Continued use constitutes acceptance.
            </p>

            <h3>14.3 Severability</h3>
            <p>
              If any provision is found invalid or unenforceable, the remaining provisions remain in full force.
            </p>

            <h3>14.4 No Waiver</h3>
            <p>
              Our failure to enforce any right or provision does not constitute a waiver of that right.
            </p>

            <h3>14.5 Assignment</h3>
            <p>
              You may not assign these Terms. We may assign our rights to a successor or affiliate.
            </p>

            <h3>14.6 Force Majeure</h3>
            <p>
              We are not liable for delays or failures due to events beyond our reasonable control (natural disasters, pandemics, cyberattacks, etc.).
            </p>
          </div>

          <div className="legal-section">
            <h2>15. Contact Information</h2>
            <p>
              For questions about these Terms, contact us at:
            </p>
            <ul>
              <li><strong>Email:</strong> legal@pearlaicare.com</li>
              <li><strong>Support:</strong> support@pearlaicare.com</li>
              <li><strong>Response Time:</strong> Within 5 business days</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>16. Acknowledgment</h2>
            <p className="warning-text">
              <strong>BY USING PEARLAI CARE, YOU ACKNOWLEDGE THAT:</strong>
            </p>
            <ul>
              <li>You have read and understood these Terms</li>
              <li>You agree to be bound by these Terms</li>
              <li>You are a licensed healthcare professional</li>
              <li>You will obtain patient consent for all recordings</li>
              <li>You will review and verify all AI-generated content</li>
              <li>You accept responsibility for all clinical decisions</li>
              <li>The Platform does not provide medical advice</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="legal-footer">
          <p>&copy; 2025 Algodentra. All rights reserved.</p>
          <p>Questions? Contact legal@pearlaicare.com</p>
        </div>
      </div>

      {showScrollTop && (
        <button
          className="scroll-to-top-btn"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  );
}

export default TermsConditions;
